import {
  IHistReactingProtocolInfo,
  IReactingProtocolStepInfoExtended,
} from '@app/core/interfaces/reacting-protocol.interface';
import dayjs from 'dayjs';
import {
  IInitEventDisruptionProtocol,
  IInitEventDisruptionProtocolHeader,
  IInitEventDisruptionProtocolStep,
} from 'ngx-mt-ui-lib/api-client';
import { TranslateService } from '@ngx-translate/core';

export interface IEventCardProtocolsSnapshot {
  activeProtocolInfo: IInitEventDisruptionProtocolHeader | null;
  activeProtocolSteps: IReactingProtocolStepInfoExtended[];
  histProtocols: IHistReactingProtocolInfo[];
}

export function prepareProtocolSteps(
  steps: IInitEventDisruptionProtocolStep[] | null | undefined,
  translate: Pick<TranslateService, 'instant'>,
): IReactingProtocolStepInfoExtended[] {
  if (!steps?.length) {
    return [];
  }

  const sortedSteps = [...steps].sort((a, b) => +a.stepNumber - +b.stepNumber);

  return sortedSteps.map((step) => {
    const parentStep = step.parentStepId
      ? sortedSteps.find((item) => item.id === step.parentStepId)
      : null;

    return {
      ...step,
      parentStepName: parentStep?.stepName,
      parentStepNumber: parentStep?.stepNumber,
      slaTime:
        step.slaMinutes < 60
          ? translate.instant('EVENT_CARD.PROTOCOL_RUNNER.SLA_UNIT', {
              value: step.slaMinutes,
            })
          : formatSlaTime(step.slaMinutes),
    };
  });
}

export function mapEventCardProtocolsSnapshot(
  protocols: readonly IInitEventDisruptionProtocol[] | null | undefined,
  translate: Pick<TranslateService, 'instant'>,
): IEventCardProtocolsSnapshot {
  if (!protocols?.length) {
    return {
      activeProtocolInfo: null,
      activeProtocolSteps: [],
      histProtocols: [],
    };
  }

  const active = protocols.find((protocol) => protocol.isActive) ?? null;
  const hist = protocols.filter((protocol) => !protocol.isActive);

  return {
    activeProtocolInfo: active ? toProtocolHeader(active) : null,
    activeProtocolSteps: active
      ? prepareProtocolSteps(active.steps, translate)
      : [],
    histProtocols: sortHistProtocols(
      hist.map((protocol) => ({
        ...toProtocolHeader(protocol),
        steps: prepareProtocolSteps(protocol.steps, translate),
      })),
    ),
  };
}

function formatSlaTime(minutes: number): string {
  const wholeMinutes = Math.max(0, Math.floor(minutes));
  const hours = Math.floor(wholeMinutes / 60);
  const rest = wholeMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

function toProtocolHeader(
  protocol: IInitEventDisruptionProtocol,
): IInitEventDisruptionProtocolHeader {
  const { steps: _steps, ...header } = protocol;
  return header;
}

function sortHistProtocols(
  histProtocols: IHistReactingProtocolInfo[],
): IHistReactingProtocolInfo[] {
  return [...histProtocols].sort((a, b) => {
    const start1 = a.launchDate ? dayjs(a.launchDate) : null;
    const start2 = b.launchDate ? dayjs(b.launchDate) : null;

    if (start1?.isSame(start2)) {
      return 0;
    }

    return start2?.isAfter(start1) ? -1 : 1;
  });
}
